import theme from 'config/theme';
import {Button} from 'interface/base/Button';
import {Icon} from 'interface/base/Icon';
import Typography from 'interface/base/Typography';
import {useEffect, useMemo, useRef, useState} from 'react';
import {StyleSheet, View} from 'react-native';

import type {MetadataState} from '../types';

interface Props {
  metadata: MetadataState;
  containerWidth?: number;
  onVisibleTemplatesChange?: (visibleTemplateIds: string[]) => void;
}

export function TemplateTabs({
  metadata,
  containerWidth,
  onVisibleTemplatesChange,
}: Props) {
  const {templates, templateId, getTemplateData, selectTemplate} = metadata;

  // Use useMemo to prevent regenerating the list on each render
  const list = useMemo(() => {
    return templates
      .map(template => getTemplateData(template.template_id))
      .filter(Boolean);
  }, [templates, getTemplateData]);

  const [visibleTemplates, setVisibleTemplates] = useState<string[]>([]);
  const containerRef = useRef<View>(null);

  // Use useRef to track previous values to prevent unnecessary updates
  const prevPropsRef = useRef({
    containerWidth,
    listLength: list.length,
    listIds: list.map(item => item?.id).join(','),
  });

  useEffect(() => {
    // Skip effect if dependencies haven't changed meaningfully
    const currentIds = list.map(item => item?.id).join(',');
    if (
      containerWidth === prevPropsRef.current.containerWidth &&
      list.length === prevPropsRef.current.listLength &&
      currentIds === prevPropsRef.current.listIds
    ) {
      return;
    }

    // Update ref with current values
    prevPropsRef.current = {
      containerWidth,
      listLength: list.length,
      listIds: currentIds,
    };

    // If we don't have a container width, assume all templates are visible
    if (!containerWidth) {
      const allTemplateIds = list
        .map(template => template?.id)
        .filter(Boolean) as string[];
      setVisibleTemplates(allTemplateIds);
      if (onVisibleTemplatesChange) {
        onVisibleTemplatesChange(allTemplateIds);
      }
      return;
    }

    // Constants for width calculation
    const buttonGap = 8;
    const baseWidth = 48; // Base button width (padding + icon + margins)
    const charWidth = 7; // Approximate width per character in pixels
    const availableWidth = containerWidth * 0.7; // Use 70% of container width for tabs

    // Calculate estimated width for each template based on name length
    const templatesWithWidth = list.map(template => ({
      id: template.id,
      name: template.name,
      // Calculate button width based on name length
      width: baseWidth + (template.name?.length ?? 0) * charWidth,
    }));

    // Find how many templates can fit within the available width
    let runningWidth = 0;
    let fitCount = 0;

    for (const template of templatesWithWidth) {
      if (runningWidth + template.width + buttonGap <= availableWidth) {
        runningWidth += template.width + buttonGap;
        fitCount++;
      } else {
        break;
      }
    }

    // Ensure at least one template is shown even if it doesn't fit
    fitCount = Math.max(1, fitCount);

    const showTemplates = list
      .slice(0, fitCount)
      .map(template => template?.id)
      .filter(Boolean) as string[];

    // Only update state if the visible templates have changed
    const currentVisibleIds = showTemplates.join(',');
    const previousVisibleIds = visibleTemplates.join(',');

    if (currentVisibleIds !== previousVisibleIds) {
      setVisibleTemplates(showTemplates);
      if (onVisibleTemplatesChange) {
        onVisibleTemplatesChange(showTemplates);
      }
    }
  }, [containerWidth, list, onVisibleTemplatesChange]);

  if (list.length === 0) {
    return null;
  }

  return (
    <View style={styles.container} ref={containerRef}>
      {list
        .filter(
          template => template?.id && visibleTemplates.includes(template.id),
        )
        .map(template => (
          <Button
            key={template.id}
            onPress={() => selectTemplate(template.id)}
            variant={templateId === template.id ? 'primary' : 'outline'}
            startEnhancer={({hovered}: {hovered: boolean}) => (
              <Icon
                name={template.icon}
                color={
                  templateId === template.id
                    ? theme.colors.neutral.$0
                    : hovered
                      ? theme.colors.neutral.$500
                      : theme.colors.neutral.$300
                }
              />
            )}
            overrides={{
              Button: {
                style:
                  templateId === template.id
                    ? {...styles.templateButton, ...styles.activeButton}
                    : styles.templateButton,
              },
            }}>
            <Typography
              size="xs"
              color={
                templateId === template.id
                  ? theme.colors.neutral.$0
                  : theme.colors.neutral.$500
              }>
              {template.name}
            </Typography>
          </Button>
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  templateButton: {
    minWidth: 40,
    borderRadius: 8,
  },
  activeButton: {
    backgroundColor: theme.colors.brand.$1_500,
  },
});
